// Admin SessionDetailPage — read-only session info + attendance list. No
// check-in controls here; tutors own that flow (apps/web/src/pages/tutor).
// Session lookup reuses the existing GET /api/sessions (find-by-id
// client-side) rather than adding a new GET /api/sessions/:id endpoint —
// the admin caller already gets every session from that one call.
import { Link, useParams } from "react-router-dom";
import { useFetch } from "../../hooks/useApi.js";
import { useSessions } from "../../contexts/SessionsContext.js";
import Badge from "../../components/shared/Badge.js";
import AttendanceStatusBadge from "../../components/shared/AttendanceStatusBadge.js";
import { IconArrowLeft } from "../../components/shared/icons.js";
import { formatDateTime } from "../../lib/formatDateTime.js";
import type { AttendanceRecordWithStudent } from "@dm-lms/shared/types";

export default function SessionDetailPage() {
  const { sessionId } = useParams<{ sessionId: string }>();

  const { sessions, isLoading: sessionsLoading, error: sessionsError } = useSessions();
  const session = sessions.find((s) => s.id === sessionId);

  const { data: attendance, isLoading: attendanceLoading, error: attendanceError } = useFetch<
    AttendanceRecordWithStudent[]
  >(sessionId ? `/api/attendance/session/${sessionId}` : null);

  return (
    <div className="dm-page">
      <Link
        to="/app/admin/sessions"
        className="mb-4 inline-flex items-center gap-1.5 text-sm text-dm-blue hover:underline"
      >
        <IconArrowLeft width={14} height={14} />
        All sessions
      </Link>

      {sessionsLoading && <p className="text-dm-slate text-sm">Loading session…</p>}
      {sessionsError && <p className="text-red-600 text-sm">{sessionsError}</p>}
      {!sessionsLoading && !sessionsError && !session && (
        <p className="text-red-600 text-sm">Session not found.</p>
      )}

      {session && (
        <div className="max-w-3xl space-y-6">
          <div className="rounded-lg border border-gray-200 bg-white p-6">
            <h1 className="mb-4 text-xl font-semibold text-dm-navy">{session.title}</h1>

            <div className="grid grid-cols-2 gap-4 text-sm sm:grid-cols-3">
              <div>
                <p className="text-xs text-dm-slate">Course</p>
                <p className="text-dm-navy">{session.courseTitle}</p>
              </div>
              <div>
                <p className="text-xs text-dm-slate">Mode</p>
                <Badge tone="gray">{session.mode}</Badge>
              </div>
              <div>
                <p className="text-xs text-dm-slate">Scheduled</p>
                <p className="text-dm-navy">{formatDateTime(session.scheduledAt)}</p>
              </div>
              <div>
                <p className="text-xs text-dm-slate">Duration</p>
                <p className="text-dm-navy">{session.durationMinutes} min</p>
              </div>
              <div>
                <p className="text-xs text-dm-slate">Venue</p>
                <p className="text-dm-navy">{session.venue ?? "—"}</p>
              </div>
              <div>
                <p className="text-xs text-dm-slate">Meeting URL</p>
                {session.meetingUrl ? (
                  <a
                    href={session.meetingUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="break-all text-dm-blue hover:underline"
                  >
                    {session.meetingUrl}
                  </a>
                ) : (
                  <p className="text-dm-navy">—</p>
                )}
              </div>
            </div>
          </div>

          <div className="rounded-lg border border-gray-200 bg-white p-6">
            <h2 className="mb-4 text-lg font-medium text-dm-navy">Attendance</h2>

            {attendanceLoading && <p className="text-sm text-dm-slate">Loading…</p>}
            {attendanceError && <p className="text-sm text-red-600">{attendanceError}</p>}

            {!attendanceLoading && !attendanceError && (attendance?.length ?? 0) === 0 && (
              <p className="text-sm text-dm-slate">No attendance recorded yet.</p>
            )}

            {!attendanceLoading && !attendanceError && (attendance?.length ?? 0) > 0 && (
              <div className="overflow-x-auto rounded-lg border border-gray-200">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50 text-left text-dm-slate">
                    <tr>
                      <th className="px-4 py-3 font-medium">Student</th>
                      <th className="px-4 py-3 font-medium">Status</th>
                      <th className="px-4 py-3 font-medium">Checked in</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {attendance!.map((record) => (
                      <tr key={record.id}>
                        <td className="px-4 py-3">
                          <p className="font-medium text-dm-navy">{record.studentName}</p>
                          <p className="text-xs text-dm-slate">{record.studentCode}</p>
                        </td>
                        <td className="px-4 py-3">
                          <AttendanceStatusBadge status={record.status} />
                        </td>
                        <td className="px-4 py-3">
                          {record.checkedInAt ? formatDateTime(record.checkedInAt) : "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
